import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import { useTranslationApp } from "../../lib/i18next";
import {
  FIRST_USER_MESSAGE,
  GREETING,
  CUSTOMER_TYPE_QUICK_QUESTIONS_MESSAGE,
  MESSAGE_TEMPLATE_UNSUPPORTED_DESCRIPTION,
  MESSAGE_TEMPLATE_UNSUPPORTED_TITLE,
} from "../../i18n/keysTranslations";
import {
  BORDER_GRAY_COLOR,
  BOX_SHADOW_FIELDS,
  GRAY_50,
  INPUT_COLOR_BORDER,
} from "../../utils/colors";
import {
  BORDER_RADIUS,
  MESSAGE_TEMPLATE_TYPE_UNSUPPORTED,
} from "../../utils/constants";

const messageBlockSx = {
  mb: 1,
  py: 2,
  px: 1.5,
  width: "100%",
  borderRadius: BORDER_RADIUS,
  bgcolor: GRAY_50,
  border: `1px solid ${BORDER_GRAY_COLOR}`,
};

const messageTextSx = {
  lineHeight: 1.6,
  color: "text.primary",
};

const messageLabelSx = {
  fontWeight: 700,
};

export const getIsUnsupportedMessageTemplate = (value) =>
  value?.customerTypeMessage === MESSAGE_TEMPLATE_TYPE_UNSUPPORTED;

// Read-only view of a Meta welcome message template (greeting + user texts).
const MessageTemplatePreview = ({ value }) => {
  const { t } = useTranslationApp();

  if (getIsUnsupportedMessageTemplate(value)) {
    const greeting = value?.rawTemplate?.text_format?.message?.text;

    return (
      <>
        <Alert
          severity="error"
          sx={{
            mb: 1,
            backgroundColor: "fields.background",
            color: "background.paperContrastText",
            border: `1px solid ${INPUT_COLOR_BORDER}`,
            borderRadius: BORDER_RADIUS,
            boxShadow: BOX_SHADOW_FIELDS,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {t(MESSAGE_TEMPLATE_UNSUPPORTED_TITLE)}
          </Typography>
          <Typography variant="body2">
            {t(MESSAGE_TEMPLATE_UNSUPPORTED_DESCRIPTION)}
          </Typography>
        </Alert>

        {greeting && (
          <Box sx={messageBlockSx}>
            <Typography variant="body2" sx={messageTextSx}>
              <Box component="span" sx={messageLabelSx}>
                {t(GREETING)}:
              </Box>{" "}
              {greeting}
            </Typography>
          </Box>
        )}
      </>
    );
  }

  const isQuickQuestions = value?.customerTypeMessage === "quickQuestions";

  return (
    <>
      <Box sx={messageBlockSx}>
        <Typography variant="body2" sx={messageTextSx}>
          <Box component="span" sx={messageLabelSx}>
            {t(GREETING)}:
          </Box>{" "}
          {value?.greeting || "-"}
        </Typography>
      </Box>

      {isQuickQuestions ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ ...messageLabelSx, mb: 1 }}>
            {t(CUSTOMER_TYPE_QUICK_QUESTIONS_MESSAGE)}:
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
            }}
          >
            {(value?.customerTexts || []).map((text, index) => (
              <Box
                key={index}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Box
                  sx={{
                    py: 0.75,
                    px: 1.5,
                    borderRadius: "16px",
                    border: `1px solid ${BORDER_GRAY_COLOR}`,
                    bgcolor: GRAY_50,
                    flex: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                    {text || "-"}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      ) : (
        <Box sx={{ ...messageBlockSx, mb: 2 }}>
          <Typography variant="body2" sx={messageTextSx}>
            <Box component="span" sx={messageLabelSx}>
              {t(FIRST_USER_MESSAGE)}:
            </Box>{" "}
            {value?.customerTexts?.[0] || "-"}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default MessageTemplatePreview;
